/**
 * One other player, as a sound.
 *
 * Everything a peer makes — their microphone if they have one, the synthesised
 * muttering in `mutter.ts` if they do not — is summed into a single chain and
 * placed in the world by it. That is deliberate: a friend on a headset and a
 * stranger breathing in the dark are the same person standing in the same
 * corridor, and they have to be attenuated, panned, filtered and sent to the
 * reverb identically or the illusion comes apart the moment one of them speaks.
 *
 *   mic  ─> voiceTrim ┐                        ┌─> dry
 *                     ├─> level ─> lp ─> pan ──┤
 *   mutter ─> mutTrim ┘                        └─> send ─> reverbIn
 *
 * The chain is the same shape as a pooled voice in `voices.ts`, and the send
 * lands on the same convolver as everything else, so a voice heard in the
 * Poolrooms is in the Poolrooms. That one connection does more for the effect
 * than any amount of work on the panning.
 *
 * `VOICE_NEAR` and `VOICE_FAR` from the wire protocol are the range: full level
 * inside the near radius, silent at the far one. Direction is the entire point
 * — hearing somebody and not knowing which way is upstairs — so the pan and the
 * shrinking bandwidth are computed by the game's own `evalSpatial`, the same
 * function that places a thud four rooms away.
 */

import { clamp, clamp01 } from '../core/math';
import type { PeerId } from '../net/protocol';
import { VOICE_FAR, VOICE_NEAR } from '../net/protocol';
import type { AudioGraph } from './graph';
import { Mutter, timbreFor, type MutterMode } from './mutter';
import { evalSpatial, makeSpatial, type Spatial } from './spatial';

/** Trim on an incoming microphone, before distance. Headroom for a loud talker. */
const VOICE_TRIM = 0.85;

/**
 * Trim on the synthesised muttering. Set against the game's own mix rather than
 * by ear alone: at the near radius it peaks a little over half a footstep, which
 * is a person you can hear in the room without being told about.
 */
const MUTTER_TRIM = 1;

/** Base reverb send, scaled by distance the way every other world sound is. */
const VOICE_SEND = 0.2;

/** Peak sample deviation that counts as somebody actually talking. */
const SPEAKING_LEVEL = 0.025;

/** Seconds between analyser reads. Cheap, and speech does not need more. */
const ANALYSE_INTERVAL = 0.12;

/** Seconds of quiet on an open microphone before their body is audible again. */
const QUIET_BEFORE_BODY = 3;

/**
 * Metres of slack past `VOICE_FAR` before the chain is considered inaudible.
 * Without it a peer walking the far radius flickers the muttering on and off.
 */
const RANGE_SLACK = 1.5;

/**
 * Shape of the roll-off between the two radii.
 *
 * Measured against the world's own curve rather than chosen for its own sake.
 * `evalSpatial` gives a footstep about 0.27 at ten metres and 0.09 at twenty;
 * a voice has to stay a little above that at every distance, because a person
 * calling out carries further than their shoes do and because a voice you can
 * only just hear is the entire effect. Squared undershoots a footstep past
 * about eighteen metres — which would be absurd — and linear leaves somebody on
 * the far side of the floor sitting up in the mix.
 */
const FALLOFF_POWER = 1.6;

/**
 * Distance attenuation for a person's voice, 1 at `VOICE_NEAR` and 0 at
 * `VOICE_FAR`. Plainly there at five metres, a rumour at twenty, gone at the
 * far radius rather than trailing off into an inaudible voice left running.
 */
export function voiceFalloff(d: number): number {
  if (d <= VOICE_NEAR) return 1;
  if (d >= VOICE_FAR) return 0;
  const t = (d - VOICE_NEAR) / (VOICE_FAR - VOICE_NEAR);
  return Math.pow(1 - t, FALLOFF_POWER);
}

export class PeerAudio {
  /** Last position we were told about, in world metres. */
  x = 0;
  z = 0;

  private readonly level: GainNode;
  private readonly lp: BiquadFilterNode;
  private readonly panner: StereoPannerNode;
  private readonly send: GainNode;
  private readonly voiceTrim: GainNode;
  private readonly mutterTrim: GainNode;
  private readonly mutter: Mutter;
  private readonly scratch: Spatial = makeSpatial();

  private source: MediaStreamAudioSourceNode | null = null;
  private analyser: AnalyserNode | null = null;
  private probe: Uint8Array<ArrayBuffer> | null = null;
  /**
   * Chrome will not pass a remote `MediaStream` through WebAudio unless the
   * stream is also attached to a media element that is playing. The element is
   * muted and produces nothing; it exists only to satisfy that, and has to be
   * held onto or it is collected and the audio stops.
   */
  private keepAlive: HTMLAudioElement | null = null;

  private analyseIn = 0;
  private quietFor = QUIET_BEFORE_BODY;
  private dist = 0;

  constructor(
    readonly id: PeerId,
    private readonly g: AudioGraph,
  ) {
    const ctx = g.ctx;

    this.lp = ctx.createBiquadFilter();
    this.lp.type = 'lowpass';
    this.lp.frequency.value = 20000;
    this.lp.Q.value = 0.4;

    this.panner = ctx.createStereoPanner();
    this.send = ctx.createGain();
    this.send.gain.value = 0;

    this.level = ctx.createGain();
    // Starts silent and ramps: a peer who appears in the list already inside
    // the near radius should fade up, not arrive as a click.
    this.level.gain.value = 0;

    this.level.connect(this.lp);
    this.lp.connect(this.panner);
    this.panner.connect(g.dry);
    this.panner.connect(this.send);
    this.send.connect(g.reverbIn);

    this.voiceTrim = ctx.createGain();
    this.voiceTrim.gain.value = VOICE_TRIM;
    this.voiceTrim.connect(this.level);

    this.mutterTrim = ctx.createGain();
    this.mutterTrim.gain.value = MUTTER_TRIM;
    this.mutterTrim.connect(this.level);

    this.mutter = new Mutter(g, this.mutterTrim, timbreFor(id));
  }

  /** True while a live remote track is wired up. */
  get hasVoice(): boolean {
    return this.source !== null;
  }

  /** Metres to the listener, as of the last `update`. */
  get distance(): number {
    return this.dist;
  }

  /**
   * Attach the remote audio. Safe to call with the same stream twice; a second
   * distinct stream replaces the first.
   */
  setStream(stream: MediaStream | null): void {
    if (!stream) {
      this.dropStream();
      return;
    }
    if (this.source && this.keepAlive?.srcObject === stream) return;
    this.dropStream();

    try {
      const el = new Audio();
      el.srcObject = stream;
      el.muted = true;
      el.autoplay = true;
      // Rejects under autoplay policy on some engines; the WebAudio path is
      // what we are actually listening to, so there is nothing to do about it.
      void el.play().catch(() => undefined);
      this.keepAlive = el;

      const src = this.g.ctx.createMediaStreamSource(stream);
      const an = this.g.ctx.createAnalyser();
      an.fftSize = 512;
      an.smoothingTimeConstant = 0;
      src.connect(an);
      src.connect(this.voiceTrim);
      this.source = src;
      this.analyser = an;
      this.probe = new Uint8Array(new ArrayBuffer(an.fftSize));
      this.quietFor = 0;
    } catch {
      // No WebAudio route to the stream. The muttering covers it, which is
      // exactly the behaviour a peer with no microphone gets.
      this.dropStream();
    }
  }

  /** Move them. Called from the network's position updates, not per frame. */
  setPosition(x: number, z: number): void {
    this.x = x;
    this.z = z;
  }

  /**
   * Per-frame. `active` is false while the game is paused or the player is
   * dead — the world stops making noise then, and a peer muttering over the
   * pause menu would be the one sound still running.
   */
  update(dt: number, active: boolean): void {
    const now = this.g.now;
    const s = evalSpatial(this.g.listener, this.x, this.z, this.scratch);
    this.dist = s.dist;

    const falloff = active ? voiceFalloff(s.dist) : 0;
    // Ramped rather than set: at fifteen position updates a second a peer
    // sprinting past would otherwise step the gain audibly.
    this.level.gain.setTargetAtTime(falloff, now, 0.05);
    this.panner.pan.setTargetAtTime(s.pan, now, 0.06);
    this.send.gain.setTargetAtTime(clamp(VOICE_SEND * s.sendScale, 0, 1.2), now, 0.08);

    // Speech gives up its consonants through a wall long before it gives up its
    // level, so a voice is filtered harder with distance than a thud is. The
    // world's own curve is still the ceiling: a peer standing behind you is
    // darker than one in front, and that cue has to survive.
    const t = clamp01((s.dist - VOICE_NEAR) / (VOICE_FAR - VOICE_NEAR));
    const speech = 9000 * Math.pow(0.09, t);
    this.lp.frequency.setTargetAtTime(Math.min(s.cutoff, speech), now, 0.1);

    this.mutter.update(dt, active ? this.mode(dt) : 'off', s.dist < VOICE_FAR + RANGE_SLACK);
  }

  dispose(): void {
    this.mutter.dispose();
    this.dropStream();
    this.voiceTrim.disconnect();
    this.mutterTrim.disconnect();
    this.level.disconnect();
    this.lp.disconnect();
    this.panner.disconnect();
    this.send.disconnect();
  }

  /* ---------------------------------------------------------------- *
   * Internals
   * ---------------------------------------------------------------- */

  /**
   * How much of a person to synthesise, decided from the signal rather than
   * from connection bookkeeping — a peer whose microphone is open but muted,
   * whose track has stalled, or who simply is not talking all look the same
   * from here, and all of them should still be a body in the room.
   *
   * The one thing this never does is put words in the mouth of somebody the
   * game can actually hear. An open microphone caps the muttering at breath and
   * movement, whatever the level says.
   */
  private mode(dt: number): MutterMode {
    if (!this.analyser || !this.probe) return 'full';

    this.analyseIn -= dt;
    if (this.analyseIn <= 0) {
      this.analyseIn = ANALYSE_INTERVAL;
      this.analyser.getByteTimeDomainData(this.probe);
      let peak = 0;
      for (let i = 0; i < this.probe.length; i++) {
        const d = Math.abs(this.probe[i]! - 128);
        if (d > peak) peak = d;
      }
      if (peak / 128 >= SPEAKING_LEVEL) this.quietFor = 0;
    }
    this.quietFor += dt;
    return this.quietFor >= QUIET_BEFORE_BODY ? 'body' : 'off';
  }

  private dropStream(): void {
    this.source?.disconnect();
    this.analyser?.disconnect();
    this.source = null;
    this.analyser = null;
    this.probe = null;
    const el = this.keepAlive;
    this.keepAlive = null;
    if (!el) return;
    try {
      el.pause();
      el.srcObject = null;
    } catch {
      /* already torn down */
    }
  }
}
